import { useRef } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";
import DramaCard from "@/components/feature/DramaCard";

type PosterCarouselProps = {
  headingId: string;
  title: string;
  subtitle?: string;
  dramas: Drama[];
  moreHref?: string;
};

export default function PosterCarousel({
  headingId,
  title,
  subtitle,
  dramas,
  moreHref,
}: PosterCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(320, track.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section aria-labelledby={headingId} className="mt-12 md:mt-16">
      <div className="mb-4 flex items-end justify-between gap-3 md:mb-6">
        <div>
          <h2
            id={headingId}
            className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl"
          >
            <span className="h-5 w-1 rounded-full bg-gradient-to-b from-secondary-300 to-primary-500 md:h-6" />
            {title}
          </h2>
          {subtitle && <p className="mt-1.5 pl-3 text-xs text-foreground-500 md:text-sm">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {moreHref && (
            <Link
              to={moreHref}
              className="hidden cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-400 transition-colors hover:text-primary-300 sm:flex"
            >
              查看全部
              <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
            </Link>
          )}
          <button
            type="button"
            aria-label="向左滑动"
            onClick={() => scrollBy(-1)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-foreground-50/15 text-foreground-200 transition-colors hover:border-primary-400 hover:text-primary-300"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="向右滑动"
            onClick={() => scrollBy(1)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-foreground-50/15 text-foreground-200 transition-colors hover:border-primary-400 hover:text-primary-300"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 md:gap-5"
      >
        {dramas.map((drama) => (
          <div key={drama.id} className="w-[42%] shrink-0 snap-start sm:w-[30%] md:w-[22%] lg:w-[15.6%]">
            <DramaCard drama={drama} />
          </div>
        ))}
      </div>
    </section>
  );
}